export const NO_MOBILE =true
export const MAX_STORED_NOTIFICATIONS = 50
export const MIN_WIDTH = 700
export const DEFAULT_SERVER_ROOT = 'https://apps.trinckle.com';
export const DEV_CDN_ROOT = 'https://hosted.trinckle.com/as-dev';
export const DEFAULT_ASSETS_PATH = `assets`;
export const DEFAULT_LANG_FILE_FOLDER_PATH = `data/lang`;

// window.location might be :
// https://hosted.trinckle.com/as-staging/apps/outlines/
// https://apps.trinckle.com/apps/outlines/
// https://localhost:1234

export const fullLocation = window.location.toString()
export const hostName = window.location.host.toString()
export const protocol = window.location.protocol // 'http:' or 'https:'

// ---------------------------------------------------------------------------
// Deployment tier — detected from the URL, immutable at runtime.
// 'production': apps.trinckle.com (or window.fakeProduction for local testing)
// 'local'     : localhost / private-network IPs
// 'staging'   : hosted.trinckle.com/as-staging/... or /as-staging-<suffix>/... (GrabCAD build)
// 'dev'       : anything else (hosted.trinckle.com/as-dev/...)
// Staging and dev share a host and differ only by path, so they are matched
// on the full URL, not the hostname.
// ---------------------------------------------------------------------------
export type DeploymentTier = 'local' |'dev'| 'staging' | 'production';

// 172.16.0.0/12 is private, the rest of 172.* is public.
const isPrivateHost = (host: string): boolean => {
  if (['localhost', '127.0.0.1'].includes(host)) return true;
  if (host.startsWith('192.168.') || host.startsWith('10.')) return true;
  const m = /^172\.(\d+)\./.exec(host);
  return !!m && +m[1] >= 16 && +m[1] <= 31;
};

export const deploymentTier: DeploymentTier = (() => {
  // @ts-ignore
  if (window.fakeProduction || fullLocation.includes('apps.trinckle.com')) return 'production';
  if (isPrivateHost(hostName.split(':')[0])) return 'local';
  if (fullLocation.includes('/as-staging')) return 'staging';
  return 'dev';
})();

// Backward-compat aliases used throughout the codebase
export const localDev     = deploymentTier === 'local';
export const isProduction = deploymentTier === 'production';

// Language controls are not customer-ready yet, so production hides them:
// as-header skips rendering <as-lang-selector>, and the LocalizationController
// stops writing ?lang= back onto the URL. Reading ?lang= still works
// everywhere — hosts like GrabCAD Print pass it in and expect it honoured.
export const SHOW_LANG_CTRLS = !isProduction;

// Manual switch for the license entries recently added to the user-options
// popout menus (as-header, as-home-header) — not yet ready for customers.
// When true, gated call sites additionally check isProduction so the entries
// still show in dev/staging.
export const HIDE_LICENSE_UX = true;

// ---------------------------------------------------------------------------
// Integration env — set explicitly via URL param or <as-app env="..."> attribute.
// Orthogonal to deploymentTier: you can be staging+grabcad, local+onprem, etc.
// No localStorage caching — env must be present on every load.
// ---------------------------------------------------------------------------
const getEnv = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const envParam = urlParams.get('env');
  if (envParam) return envParam;

  const asApp = document.querySelector('as-app');
  const attrParam = asApp?.getAttribute('env');
  if (attrParam) return attrParam;

  return null;
};

// The env set explicitly. [DEPRECATED] env="onprem" was the on-prem switch;
// it is deployment="onprem" now and env names the host again, so the legacy
// value is not passed on as an env and the host is detected as usual.
const explicitEnv = getEnv();
const legacyOnPremEnv = explicitEnv === 'onprem';
if (legacyOnPremEnv) console.warn('[DEPRECATED] env="onprem" is deployment="onprem" now. The env is detected as usual.');
export const currentEnv = legacyOnPremEnv ? null : explicitEnv;

// ---------------------------------------------------------------------------
// Deployment: where the suite is installed. 'onprem' is a customer's own
// server, with no trinckle services. Set by ?deployment= or
// <as-app deployment="onprem">, read on every load, never detected. Separate
// from env, so an on-prem install can still run inside a host like GrabCAD.
// ---------------------------------------------------------------------------
export type Deployment = 'cloud' | 'onprem';

const getDeployment = (): Deployment => {
  const value = new URLSearchParams(window.location.search).get('deployment')
    ?? document.querySelector('as-app')?.getAttribute('deployment');
  return value === 'onprem' || legacyOnPremEnv ? 'onprem' : 'cloud';
};

export const deployment: Deployment = getDeployment();
export const isOnPrem = deployment === 'onprem';
/** @deprecated Use `isOnPrem`. */
export const suppressExternalServices = isOnPrem;

// ---------------------------------------------------------------------------
// Language forced by the host via URL, e.g. GrabCAD Print launches the app
// with its own ?lang= appended since it manages language itself. Generic and
// env-independent — any host that appends this param is picked up the same
// way, no per-env code needed. See docs/localization.md.
// ---------------------------------------------------------------------------
export const urlLang: string | null = new URLSearchParams(window.location.search).get('lang');

// ---------------------------------------------------------------------------
// Last language the user actually picked, persisted across sessions so an
// app launched without ?lang= (and without an explicit `lang` attribute on
// the host markup) reopens in the same language instead of resetting to the
// LocalizationController's 'en' default. Written back out by
// LocalizationController.updateLang whenever the language changes. Wrapped
// in a try/catch since localStorage can throw in sandboxed iframes (e.g.
// as-code-demo's srcdoc sandbox).
// ---------------------------------------------------------------------------
export const LANG_STORAGE_KEY = 'as-app-lang';
export const storedLang: string | null = (() => {
  try { return localStorage.getItem(LANG_STORAGE_KEY); } catch { return null; }
})();

// True when running inside any iframe (e.g. as-code-demo srcdoc sandbox).
// window.location.protocol === 'about:' reliably identifies srcdoc/blank iframes
// regardless of sandbox allow-same-origin quirks that can make window.self === window.top.
export const inIframe = (() => {
  if (window.location.protocol === 'about:') return true;
  try { return window.self !== window.top; } catch { return true; }
})();

// True when window.location.pathname tells us nothing about where the suite is
// deployed: about:srcdoc / about:blank documents report a pathname of 'srcdoc'
// or 'blank'. A framed page loaded from a real URL is not in this position -
// its pathname is as good as a top-level one.
export const hasUsablePathname = window.location.protocol !== 'about:';

// True when external API calls should be suppressed (e.g. as-code-demo sandbox sets window.noAPI = true)
export const noAPI: boolean = (() => {
  if (window.noAPI) return true;
  try { return window.parent !== window && !!window.parent?.noAPI; } catch { return false; }
})();



export const useLocalAssets = !!document.querySelector('as-app[use-local-assets]');


// Get first path segment (e.g., 'as-staging' from '/as-staging/apps/...')
// Excludes file-name segments (anything with a dot, like 'tutorial.html')
export const pathNameParts = window.location.pathname.split("/").filter(x => x.length && !x.includes('.'));
// Production serves the suite from the domain root, so there is no prefix to
// find. Everywhere else the first path segment is it - including inside an
// iframe, as long as the frame has a real URL to read. Only about:srcdoc and
// about:blank documents have to be skipped: their pathname ('srcdoc') would be
// mistaken for a deployment prefix and every suite URL would 404.
// On-prem, the suite can sit anywhere on the customer's server: its root is
// whatever comes before /apps/, or the domain root.
const onpremRootPath = (): string => {
  const path = window.location.pathname;
  const i = path.indexOf('/apps/');
  return i > 0 ? path.slice(1, i) : '';
};
export const appSuiteRootPath = isOnPrem ? onpremRootPath()
  : (isProduction || !hasUsablePathname) ? '' : (pathNameParts[0] || '');

// Full base URL with protocol and optional path prefix
export const fullAppSuiteRootURL = appSuiteRootPath
  ? `${protocol}//${hostName}/${appSuiteRootPath}`
  : `${protocol}//${hostName}`;

/**
 * The URL of a suite page, given its folder. On-prem the server may not serve
 * a folder's index.html by itself, so the file name is spelled out. The cloud
 * tiers keep the folder URL.
 */
export const suitePageUrl = (folderUrl: string, search = ''): string =>
  `${folderUrl}${isOnPrem ? '/index.html' : ''}${search}`;

// API endpoint
export const apiRoot = (localDev && !isOnPrem)
  ? 'https://hosted.trinckle.com/as-staging/user/api.php'
  : appSuiteRootPath
    ? `/${appSuiteRootPath}/user/api.php`
    : '/user/api.php';



export const DEFAULT_PARAMATE_API_HUB = 'https://api.paramate.trinckle.com';


export const serverCodes = {
  "ERRORCODE_NO_ERROR": -1,
  "ERRORCODE_UNKNOWN_ERROR": 0,
  "ERRORCODE_WRONG_CREDENTIALS": 1,
  "ERRORCODE_NOT_ACTIVE": 2,
  "ERRORCODE_WEAK_PASSWORD": 3,
  "ERRORCODE_PASSWORD_NOT_CONFIRMED": 4,
  "ERRORCODE_ALREADY_EXISTS": 5,
  "ERRORCODE_NOT_ALLOWED": 6,
  "ERRORCODE_NO_ACCESS": 7,
  "ERRORCODE_NOT_FOUND": 8,
  "ERRORCODE_INVALID": 9,
  "ERRORCODE_CAPTCHA": 101,
  "ERRORCODE_EMAIL": 102,

  "-1": "ERRORCODE_NO_ERROR",
  "0": "ERRORCODE_UNKNOWN_ERROR",
  "1": "ERRORCODE_WRONG_CREDENTIALS",
  "2": "ERRORCODE_NOT_ACTIVE",
  "3": "ERRORCODE_WEAK_PASSWORD",
  "4": "ERRORCODE_PASSWORD_NOT_CONFIRMED",
  "5": "ERRORCODE_ALREADY_EXISTS",
  "6": "ERRORCODE_NOT_ALLOWED",
  "7": "ERRORCODE_NO_ACCESS",
  "8": "ERRORCODE_NOT_FOUND",
  "9": "ERRORCODE_INVALID",
  "101": "ERRORCODE_CAPTCHA",
  "102": "ERRORCODE_EMAIL",
};
